import {isString, isPlainObject, isError} from 'lodash'
// @ts-ignore
import JSON_ from 'json_'


const startTime = new Date()

type HTTP_METHOD = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

type RequestBody = object | FormData

export interface IApiConnectionInjection {
  apiConnection: ApiConnection
}

interface ApiOptions {
  isNextPageUrl?: boolean
  admin?: boolean
  method?: HTTP_METHOD
  useCache?: boolean
  body?: RequestBody
  version?: 1|2
  isFormData?: boolean
  transformResponseToCamelCase?: boolean
  processJsonResponse?: boolean
  includeResponseStatus?: boolean
}

interface ResponseData {
  method: HTTP_METHOD
  url: string
  body?: RequestBody
  headers: Headers
  elapsedSessionSeconds?: number
  responseStatus?: number
  responseStatusText?: string
  responseType?: ResponseType
  responseText?: string
  error?: any
}

export class ApiConnection {

  private readonly apiHost: string
  private readonly cdnApiHost: string
  private readonly cdnPath: string
  private readonly sessionId: string
  private readonly csrfToken: string
  private readonly contentPermissionId: string
  private readonly logger: any


  constructor(options: {apiHost: string, cdnApiHost: string, cdnPath: string, sessionId: string, csrfToken: string, contentPermissionId: string}, logger: any = console) {
    const {apiHost, cdnApiHost, cdnPath, sessionId, csrfToken, contentPermissionId} = options
    this.apiHost = apiHost || ''
    this.cdnApiHost = cdnApiHost || this.apiHost
    this.cdnPath = cdnPath || ''
    this.sessionId = sessionId
    this.csrfToken = csrfToken
    this.contentPermissionId = contentPermissionId || ''

    this.logger = logger
  }


  public get<T>(path: string, options: ApiOptions= {}) {
    return this.api<T>(path, {...options, method: 'GET'})
  }

  public post<T>(path: string, body?: RequestBody, options: ApiOptions= {}) {
    return this.api<T>(path, {...options, method: 'POST', body})
  }

  public delete<T>(path: string, options: ApiOptions= {}) {
    return this.api<T>(path, {...options, method: 'DELETE', processJsonResponse: false})
  }

  public put<T>(path: string, body?: RequestBody, options: ApiOptions= {}) {
    return this.api<T>(path, {...options, method: 'PUT', body})
  }

  public patch<T>(path: string, body?: RequestBody, options: ApiOptions= {}) {
    return this.api<T>(path, {...options, method: 'PATCH', body})
  }

  private api<T>(path: string, {isNextPageUrl= false, admin= false, method= 'GET', useCache= false, body, version= 2, isFormData= false,
    transformResponseToCamelCase= true, processJsonResponse= true, includeResponseStatus= false}: ApiOptions = {}): Promise<T> {
    const isGet = method === 'GET'
    const headers = new Headers()

    // Credentials is needed when:
    const isCredentialsNeeded =
      !this.sessionId && // We don't need credentials in the localhost
      // and if we're not in the localhost, we do want the credentials if
      (!useCache || !isGet) // we don't use cache or we don't do GET request

    if (!isFormData) {
      // we use text/plain for GET requests to prevent wastefull CORS OPTIONS requests
      // see more https://m.alphasights.com/killing-cors-preflight-requests-on-a-react-spa-1f9b04aa5730
      const contentType = isGet ? 'text/plain' : 'application/json'
      headers.append('Content-Type', contentType)
    }

    headers.append('Accept', 'application/json')

    if (this.sessionId && !(useCache && isGet)) {
      headers.append('X-Session', this.sessionId)
    }

    if (this.csrfToken && !isGet) {
      headers.append('X-CSRFToken', this.csrfToken)
    }

    const options: RequestInit = {
      method,
      headers,
      body: isFormData ? body : body && JSON.stringify(body) as any,
      credentials: isCredentialsNeeded ? 'include' : undefined
    }

    const host = isGet && useCache ? this.cdnApiHost : this.apiHost

    const getApiUrl = () => {

      if (admin) {
        return `${host}/admin/api/v${version}/${path}`
      }

      if (isNextPageUrl) {
        // TODO: change this after fix of all api page STDU-???
        return path.includes(host) ? path : `${host}${path}`
      } else {
        return `${host}/api/v${version}/${path}`
      }
    }

    const url = getApiUrl()

    const lastFailedResponse: ResponseData = {url, body, method, headers}
    lastFailedResponse.elapsedSessionSeconds = ((new Date() as any as number) - (startTime as any as number)) / 1000

    return fetch(url, options).then((response: Response) => {
      if (!response.ok) {

        lastFailedResponse.responseStatus = response.status
        lastFailedResponse.responseStatusText = response.statusText
        lastFailedResponse.responseType = response.type
        lastFailedResponse.headers = response.headers


        if (response.status === 500) {
          return Promise.reject(includeResponseStatus ? {error: 'Encountered internal server error', status: response.status} : undefined)
        }

        return response.text().then((text: string) => {
          lastFailedResponse.responseText = lastFailedResponse.responseStatus === 404 ? 'NA' : text
          return Promise.reject(includeResponseStatus ? {error: text, status: response.status} : text)
        })
      }

      if (response.status === 204) {
        return includeResponseStatus ? Promise.resolve({value: null, status: response.status}) : null
      }

      let responseValuePromise
      if (processJsonResponse) {
        responseValuePromise = transformResponseToCamelCase ?
          response.text().then(snakeJson => JSON_.parse(snakeJson)) :
          response.json()
      } else {
        responseValuePromise = response.text()
      }
      return includeResponseStatus ? responseValuePromise.then(value => ({value, status: response.status})) : responseValuePromise
    }).catch(e => {
      lastFailedResponse.error = e
      let error
      if (isError(e)) {
        error = e
      } else if (isPlainObject(e)) {
        error = new Error(`Fetch Failed: ${JSON.stringify(e)}`)
      } else if (isString(e)) {
        error = new Error(`Fetch Failed: ${e}`)
      } else {
        error = new Error('Fetch failed.')
      }
      this.logger.error(error, lastFailedResponse)
      return Promise.reject(e || error)
    })
  }
}

